import { useEffect } from "react";
import { useForm } from "react-hook-form";
import classes from "./blogDetailForm.module.css";
import { FormInput } from "../../shared/formElements/formInput/FormInput";
import { FormCheckboxChips } from "../../shared/formElements/formCheckBoxChip/FormCheckBoxChip";
import { FormImagePreviewAndUpload } from "../../shared/formElements/FormImagePreviewAndUpload/FormImagePreviewAndUpload";
import { FormCalendar } from "../../shared/formElements/formCalendar/FormCalendar";
import { FormTextarea } from "../../shared/formElements/formTextarea/FormTextarea";
import { FormRichText } from "../../shared/formElements/formRichText/FormRichtext";

export const BlogDetailForm = ({
  initialValues,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: "onChange",
  });

  // Register ReactQuill manually for required validation
  useEffect(() => {
    reset(initialValues);
    register("description", {
      required: "Blog description is required",
      validate: (val) => {
        const text = new DOMParser().parseFromString(val || "", "text/html")
          .body.textContent.trim();
        return text.length > 0 || "Blog description is required";
      },
    });
  }, [initialValues, reset, register]);

  const bannerUrl = watch("banner_image_url");

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      {/* -------------------- BASIC INFO -------------------- */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Blog Information</h3>

        <div className={classes.grid}>
          <FormInput
            id="title"
            label="Title"
            register={register}
            validation={{ required: "Title is required" }}
            error={errors.title?.message}
            required
          />
          <FormInput
            id="category"
            label="Category"
            register={register}
            validation={{ required: "Category is required" }}
            error={errors.category?.message}
            required
          />
          <FormCalendar
            id="publishing_date"
            label="Publishing Date"
            watch={watch}
            setValue={setValue}
            register={register}
            required
            error={errors.publishing_date?.message}
          />
          <FormInput
            id="slug"
            label="Slug"
            register={register}
            validation={{ required: "Slug is required" }}
            error={errors.slug?.message}
            required
          />
          <FormInput
            id="seo_title"
            label="SEO Title"
            register={register}
            validation={{ required: "SEO Title is required" }}
            error={errors.seo_title?.message}
            required
          />
          <FormCheckboxChips
            id="status"
            label="Status"
            options={[
              { id: "Published", label: "Published" },
              { id: "Draft", label: "Draft" },
            ]}
            watch={watch}
            setValue={setValue}
            register={register}
            validation={{ required: "Status is required" }}
            error={errors.status?.message}
            required
          />
          <FormCheckboxChips
            id="is_featured"
            label="Blog Type"
            options={[
              { id: 0, label: "Normal" },
              { id: 1, label: "Featured" },
            ]}
            watch={watch}
            setValue={setValue}
            register={register}
            validation={{ required: "Status is required" }}
            error={errors.status?.message}
            required
          />
        </div>
        <FormTextarea
          id="seo_description"
          label="SEO Description"
          register={register}
          validation={{ required: "SEO Description is required" }}
          error={errors.seo_description?.message}
          required
        />
      </div>

      {/* -------------------- DESCRIPTION -------------------- */}
      <div className={classes.section}>
        <FormRichText
          id="description"
          label="Blog Description"
          value={watch("description")}
          onChange={(val) =>
            setValue("description", val, { shouldValidate: true })
          }
          required
          error={errors.description?.message}
        />
      </div>

      {/* -------------------- BANNER IMAGE -------------------- */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Banner Image</h3>

        <FormImagePreviewAndUpload
          id="banner_image_url"
          url={bannerUrl}
          register={register}
          setValue={setValue}
          validation={{ required: "Banner image is required" }}
          error={errors.banner_image_url?.message}
          minWidth={550}
          minHeight={550}
        />
      </div>

      {/* -------------------- ACTION BUTTONS -------------------- */}
      <div className={classes.actions}>
        <button
          type="button"
          className={classes.deleteBtn}
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>

        <button
          type="submit"
          className={classes.submitBtn}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update Blog"}
        </button>
      </div>
    </form>
  );
};
